import { __decorate, __metadata } from "tslib";
import { Fingerprintable, IsNotEmptyObjectWithLVS, IsUndefinable, PromotionExternalAmountTypeEnum, SupportedCurrencyEnum, instanceToPlainWithLVS, } from '@goparrot/common';
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import { Expose, Type } from 'class-transformer';
import { IsArray, IsBoolean, IsEnum, IsInt, IsNotEmpty, IsNumber, IsOptional, IsString, IsUUID, Min, ValidateIf, ValidateNested } from 'class-validator';
import { PromotionAmountTypeEnum, PromotionDiscountBehaviourEnum, PromotionDiscountLevelEnum, PromotionSubTypeEnum, PromotionTagEnum, PromotionTypeEnum, } from '../../common';
import { PromotionExposeGroup } from '../interface';
import { ReadPromocodeTemplateUsageRulesDto } from './ReadPromocodeTemplateUsageRulesDto';
import { ReadPromotionConditionsDto } from './ReadPromotionConditionsDto';
import { ReadPromotionDiscountDto } from './ReadPromotionDiscountDto';
import { ReadPromotionImagesDto } from './ReadPromotionImagesDto';
import { ReadPromotionMerchantGroupDto } from './ReadPromotionMerchantGroupDto';
import { ReadPromotionProviderDto } from './ReadPromotionProviderDto';
let ReadPromotionDto = class ReadPromotionDto {
    appliesToModifiers() {
        var _a;
        return (_a = this.conditions.appliesToModifiers) !== null && _a !== void 0 ? _a : true;
    }
    isCheckBased() {
        return PromotionDiscountLevelEnum.CHECK_BASED === this.discount.level;
    }
    isItemBased() {
        return [PromotionDiscountLevelEnum.ITEM_BASED, PromotionDiscountLevelEnum.BOGO, PromotionDiscountLevelEnum.COMBO].includes(this.discount.level);
    }
    isFixed() {
        return PromotionAmountTypeEnum.FIXED === this.discount.amountType;
    }
    isPercentage() {
        return PromotionAmountTypeEnum.PERCENT === this.discount.amountType;
    }
    isFinalPrice() {
        return PromotionAmountTypeEnum.FINAL_PRICE === this.discount.amountType;
    }
    isPromoCodePromotion() {
        return this.subtype === PromotionSubTypeEnum.PROMOCODE && this.isPromoCode && !!this.promoCode;
    }
    isLoyaltyReward() {
        return PromotionSubTypeEnum.LOYALTY === this.subtype;
    }
    isHighOrderPromotion() {
        return this.isPromoCodePromotion() || this.isLoyaltyReward() || (this.isItemBased() && this.hasAddItemBehavior());
    }
    hasAddItemBehavior() {
        return this.discount.behaviour === PromotionDiscountBehaviourEnum.ADD_ITEM;
    }
    hasNoBehavior() {
        return PromotionDiscountBehaviourEnum.NO_BEHAVIOUR === this.discount.behaviour;
    }
    hasMaxAmount() {
        return this.discount.maxAmount > 0;
    }
    isMultipleApplicable() {
        return ((!this.conditions.maxUsagesPerCart || this.conditions.maxUsagesPerCart > 1) &&
            ((this.isItemBased() && this.hasAddItemBehavior()) || (this.isCheckBased() && this.hasNoBehavior() && this.isFixed())));
    }
    hasTag(tag) {
        return this.tags.includes(tag);
    }
    getExternalType() {
        if (this.hasTag(PromotionTagEnum.CAPABILITY_POS_AUTO_APPLICABLE)) {
            return this.isPercentage() ? PromotionExternalAmountTypeEnum.PERCENT : PromotionExternalAmountTypeEnum.FIXED;
        }
        if (this.isCheckBased()) {
            if (this.isPercentage()) {
                return this.hasMaxAmount() || !this.isCumulative || !this.appliesToModifiers()
                    ? PromotionExternalAmountTypeEnum.OPEN
                    : PromotionExternalAmountTypeEnum.PERCENT;
            }
            if (this.isFixed()) {
                return this.isMultipleApplicable() || !this.appliesToModifiers() ? PromotionExternalAmountTypeEnum.OPEN : PromotionExternalAmountTypeEnum.FIXED;
            }
        }
        if (this.isItemBased()) {
            if (this.isPercentage()) {
                return this.hasMaxAmount() || !this.appliesToModifiers() ? PromotionExternalAmountTypeEnum.OPEN : PromotionExternalAmountTypeEnum.PERCENT;
            }
            if (this.isFixed()) {
                return !this.appliesToModifiers() ? PromotionExternalAmountTypeEnum.OPEN : PromotionExternalAmountTypeEnum.FIXED;
            }
            if (this.isFinalPrice()) {
                return PromotionExternalAmountTypeEnum.OPEN;
            }
        }
        throw new Error('Unexpected promotion configuration');
    }
    getFingerprint() {
        return instanceToPlainWithLVS(this, { groups: [PromotionExposeGroup.PROFILER] });
    }
};
__decorate([
    ApiProperty({ type: () => String, format: 'uuid', description: 'uuid v4', example: 'e12acc6f-2f34-442c-92d3-d4199fb12345' }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.FIXTURE, PromotionExposeGroup.PROFILER] }),
    IsNotEmpty({ always: true }),
    IsUUID('4', { always: true }),
    __metadata("design:type", String)
], ReadPromotionDto.prototype, "uuid", void 0);
__decorate([
    ApiProperty({ type: () => String, description: 'PromoCode, as in 10LUCKY ', example: 'MISSU10' }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsNotEmpty({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.PUT] }),
    IsString({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.PUT] }),
    __metadata("design:type", String)
], ReadPromotionDto.prototype, "promotionId", void 0);
__decorate([
    ApiProperty({ type: () => String, description: 'promotion uniqueName, (is in use somewhere) or can be uuid/guid', example: 'MISSU10_CHELSEA' }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsUndefinable({ groups: [PromotionExposeGroup.CREATE] }),
    IsNotEmpty({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.PUT] }),
    IsString({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.PUT] }),
    __metadata("design:type", String)
], ReadPromotionDto.prototype, "uniqueName", void 0);
__decorate([
    ApiProperty({ type: () => String, description: 'merchant key', example: 'WESTVILLE_GROUP' }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.FIXTURE] }),
    IsNotEmpty({ groups: [PromotionExposeGroup.READ] }),
    IsString({ groups: [PromotionExposeGroup.READ] }),
    __metadata("design:type", String)
], ReadPromotionDto.prototype, "merchantId", void 0);
__decorate([
    ApiProperty({ type: () => Boolean, description: 'is this promotion used as promo-code' }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsNotEmpty({ always: true }),
    IsBoolean({ always: true }),
    __metadata("design:type", Boolean)
], ReadPromotionDto.prototype, "isPromoCode", void 0);
__decorate([
    ApiPropertyOptional({ type: () => String, description: 'promoCode text', example: 'TEST10VIP' }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    ValidateIf((o) => !!(o === null || o === void 0 ? void 0 : o.isPromoCode), { always: true }),
    IsNotEmpty({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.PUT] }),
    IsString({ always: true }),
    __metadata("design:type", String)
], ReadPromotionDto.prototype, "promoCode", void 0);
__decorate([
    ApiProperty({ type: () => Boolean, description: 'is promotion active' }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsBoolean({ always: true }),
    __metadata("design:type", Boolean)
], ReadPromotionDto.prototype, "isActive", void 0);
__decorate([
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsNotEmpty({ always: true }),
    IsBoolean({ always: true }),
    IsUndefinable({ always: true }),
    __metadata("design:type", Boolean)
], ReadPromotionDto.prototype, "isTemplate", void 0);
__decorate([
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    Type(() => ReadPromocodeTemplateUsageRulesDto),
    IsOptional({ always: true }),
    IsNotEmptyObjectWithLVS({ always: true }),
    ValidateNested({ always: true }),
    __metadata("design:type", ReadPromocodeTemplateUsageRulesDto)
], ReadPromotionDto.prototype, "templateUsageRules", void 0);
__decorate([
    ApiProperty({ type: () => String, description: 'Promotion type', default: PromotionTypeEnum.PROMOTION, example: PromotionTypeEnum.PROMOTION }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsString({ always: true }),
    __metadata("design:type", String)
], ReadPromotionDto.prototype, "type", void 0);
__decorate([
    ApiProperty({
        enum: PromotionSubTypeEnum,
        description: `subtype of promotion -  ${Object.values(PromotionSubTypeEnum).join(' | ')}`,
        example: PromotionSubTypeEnum.LOYALTY,
    }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsEnum(PromotionSubTypeEnum, { always: true }),
    __metadata("design:type", String)
], ReadPromotionDto.prototype, "subtype", void 0);
__decorate([
    ApiProperty({ type: () => String, description: 'arbitrary text, short title', example: '10% Off' }),
    Expose({
        groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE, PromotionExposeGroup.PROFILER],
    }),
    IsNotEmpty({ always: true }),
    IsString({ always: true }),
    __metadata("design:type", String)
], ReadPromotionDto.prototype, "title", void 0);
__decorate([
    ApiPropertyOptional({ type: () => String, description: 'arbitrary text, subtitle', example: 'Get 10% Off Your Online Order!' }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsOptional({ always: true }),
    IsString({ always: true }),
    __metadata("design:type", String)
], ReadPromotionDto.prototype, "subtitle", void 0);
__decorate([
    ApiPropertyOptional({ type: () => String, description: 'arbitrary text', example: '15% discount on your first order.' }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsOptional({ always: true }),
    IsString({ always: true }),
    __metadata("design:type", String)
], ReadPromotionDto.prototype, "message", void 0);
__decorate([
    ApiProperty({ type: () => ReadPromotionImagesDto }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    Type(() => ReadPromotionImagesDto),
    ValidateNested({ always: true }),
    __metadata("design:type", ReadPromotionImagesDto)
], ReadPromotionDto.prototype, "images", void 0);
__decorate([
    ApiProperty({ type: () => ReadPromotionConditionsDto }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    Type(() => ReadPromotionConditionsDto),
    ValidateNested({ always: true }),
    __metadata("design:type", ReadPromotionConditionsDto)
], ReadPromotionDto.prototype, "conditions", void 0);
__decorate([
    ApiProperty({ enum: SupportedCurrencyEnum, description: 'currency of the cart/cart-item, constant, always "USD" ', example: SupportedCurrencyEnum.USD }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsEnum(SupportedCurrencyEnum, { always: true }),
    __metadata("design:type", String)
], ReadPromotionDto.prototype, "currency", void 0);
__decorate([
    ApiProperty({ type: () => Number, description: 'price of the cart-item, constant, always "0.0" ', example: 0.0 }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsNumber({}, { always: true }),
    __metadata("design:type", Number)
], ReadPromotionDto.prototype, "price", void 0);
__decorate([
    ApiProperty({ type: () => ReadPromotionDiscountDto }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    Type(() => ReadPromotionDiscountDto),
    ValidateNested({ always: true }),
    __metadata("design:type", ReadPromotionDiscountDto)
], ReadPromotionDto.prototype, "discount", void 0);
__decorate([
    ApiProperty({ type: () => ReadPromotionProviderDto }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    Type(() => ReadPromotionProviderDto),
    ValidateNested({ always: true }),
    __metadata("design:type", ReadPromotionProviderDto)
], ReadPromotionDto.prototype, "provider", void 0);
__decorate([
    ApiProperty({ type: () => Number }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsUndefinable({ groups: [PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT] }),
    IsInt({ groups: [PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT] }),
    Min(1, { groups: [PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT] }),
    __metadata("design:type", Number)
], ReadPromotionDto.prototype, "priority", void 0);
__decorate([
    ApiProperty({ type: () => Boolean }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsUndefinable({ groups: [PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT] }),
    IsBoolean({ groups: [PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT] }),
    __metadata("design:type", Boolean)
], ReadPromotionDto.prototype, "isCumulative", void 0);
__decorate([
    ApiProperty({ type: () => String, isArray: true }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsUndefinable({ groups: [PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT] }),
    IsString({ each: true, groups: [PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT] }),
    IsNotEmpty({ each: true, groups: [PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT] }),
    __metadata("design:type", Array)
], ReadPromotionDto.prototype, "compatible", void 0);
__decorate([
    ApiProperty({ type: () => ReadPromotionMerchantGroupDto }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    Type(() => ReadPromotionMerchantGroupDto),
    ValidateNested({ always: true }),
    __metadata("design:type", ReadPromotionMerchantGroupDto)
], ReadPromotionDto.prototype, "group", void 0);
__decorate([
    ApiProperty({ type: () => String, isArray: true }),
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.CREATE, PromotionExposeGroup.PUT, PromotionExposeGroup.FIXTURE] }),
    IsArray({ always: true }),
    IsString({ always: true, each: true }),
    __metadata("design:type", Array)
], ReadPromotionDto.prototype, "tags", void 0);
__decorate([
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.FIXTURE] }),
    Type(() => Date),
    __metadata("design:type", Date)
], ReadPromotionDto.prototype, "createdAt", void 0);
__decorate([
    Expose({ groups: [PromotionExposeGroup.READ, PromotionExposeGroup.FIXTURE] }),
    Type(() => Date),
    __metadata("design:type", Date)
], ReadPromotionDto.prototype, "updatedAt", void 0);
ReadPromotionDto = __decorate([
    Fingerprintable()
], ReadPromotionDto);
export { ReadPromotionDto };
//# sourceMappingURL=ReadPromotionDto.js.map